import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { slackApi, type UpdateSlackPreferences } from "@/lib/api/slack";

export const slackKeys = {
  status: ["slack", "status"] as const,
  channels: ["slack", "channels"] as const,
  link: ["slack", "link"] as const,
  preferences: ["slack", "preferences"] as const,
};

/** The organization's Slack app: configured, installed, notify channel, and whether the caller may manage it. */
export function useSlackStatus() {
  return useQuery({
    queryKey: slackKeys.status,
    queryFn: async () => (await slackApi.status()).data,
  });
}

/** Public channels for the admin's notify-channel picker. */
export function useSlackChannels(enabled: boolean) {
  return useQuery({
    queryKey: slackKeys.channels,
    queryFn: async () => (await slackApi.channels()).data,
    enabled,
    staleTime: 60_000,
  });
}

/** The signed-in user's own Slack link. */
export function useSlackLink(enabled = true) {
  return useQuery({
    queryKey: slackKeys.link,
    queryFn: async () => (await slackApi.getLinkStatus()).data,
    enabled,
  });
}

/** The signed-in user's notification preferences; only meaningful once linked. */
export function useSlackPreferences(enabled: boolean) {
  return useQuery({
    queryKey: slackKeys.preferences,
    queryFn: async () => (await slackApi.getPreferences()).data,
    enabled,
  });
}

/** Admin: fetch the "Add to Slack" URL and send the browser there. */
export function useInstallSlack() {
  return useMutation({
    mutationFn: async () => (await slackApi.installUrl()).data.url,
    onSuccess: (url) => {
      window.location.assign(url);
    },
  });
}

/** Admin: disconnect the workspace. Links and pending deliveries go with it. */
export function useDisconnectSlack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => slackApi.disconnect(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["slack"] });
    },
  });
}

export function useSetSlackNotifyChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string | null) => slackApi.setNotifyChannel(channelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: slackKeys.status });
    },
  });
}

/**
 * Deep links into the workspace plus instructions: the user runs /compbox
 * there and gets a one-time link back. The link status refetches on focus.
 */
export function useStartSlackLink() {
  return useMutation({
    mutationFn: async () => (await slackApi.startLink()).data,
  });
}

export function useUnlinkSlack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => slackApi.unlink(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: slackKeys.link });
      queryClient.removeQueries({ queryKey: slackKeys.preferences });
    },
  });
}

export function useUpdateSlackPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSlackPreferences) => (await slackApi.updatePreferences(input)).data,
    onSuccess: (prefs) => {
      queryClient.setQueryData(slackKeys.preferences, prefs);
    },
  });
}
